/**
 * Zod schemas for the admission seam. It crosses a wire twice: the campaign dialler in
 * `apps/worker` requests admission across a process boundary, and the Staff capacity screen
 * reads the decision through the API.
 */
import { z } from "zod";
import { idSchema } from "../id/schema.js";
import { accountId, agentVersionId, conversationId, gpuPoolId, workspaceId } from "../id/codec.js";
import { ADMISSION_REFUSAL_REASON } from "./admission.js";
import { CONVERSATION_DIRECTION, MEDIUM } from "./medium.js";
import { MODEL_BACKUP_CODE } from "./usage.js";
const LANGUAGE_CODE = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;
export const MediumSchema = z.enum(MEDIUM);
export const ConversationDirectionSchema = z.enum(CONVERSATION_DIRECTION);
export const AdmissionRequestSchema = z
    .object({
    accountId: idSchema(accountId),
    workspaceId: idSchema(workspaceId),
    agentVersionId: idSchema(agentVersionId),
    language: z.string().regex(LANGUAGE_CODE, "not a BCP-47 language code"),
    direction: ConversationDirectionSchema,
    medium: MediumSchema,
    conversationId: idSchema(conversationId),
    // plan-014 E3a (2026-10-06): the reservation reads it with the medium; absent is a real conversation.
    isTest: z.boolean().optional(),
})
    .strict();
/** The refusal arm is a domain outcome, not an error envelope (design 9.2). */
/** plan-010 (design §24.3.1): a placement as a model route — a pool, or the model backup. */
export const ModelRouteSchema = z.union([
    z.object({ kind: z.literal("gpu_pool"), gpuPoolId: idSchema(gpuPoolId) }).strict(),
    // plan-027 (design §38.3): the backup's code. A decision from a runtime built before plan-027 names no code; that
    // build knows one backup only, the first, so its decision is read as the first's — the reading of an earlier
    // build's wire, never a configuration default (the migration's fill trigger reads its rows the same way).
    z
        .object({ kind: z.literal("model_backup"), modelBackupCode: z.enum(MODEL_BACKUP_CODE).optional() })
        .strict()
        .transform((route) => ({ kind: route.kind, modelBackupCode: route.modelBackupCode === undefined ? "first" : route.modelBackupCode })),
]);
export const AdmissionDecisionSchema = z.union([
    // plan-007 (design §19, IC-63): the gpu_pool the conversation was placed on; optional so a decision from
    // a pre-plan-007 runtime (no placement) still parses. plan-010 (design §24.4): the placement as a model
    // route, optional for the same reason.
    z
        .object({
        admitted: z.literal(true),
        channelUnitHeld: z.literal(1),
        gpuPoolId: idSchema(gpuPoolId).optional(),
        modelRoute: ModelRouteSchema.optional(),
    })
        .strict(),
    z.object({ admitted: z.literal(false), reason: z.enum(ADMISSION_REFUSAL_REASON) }).strict(),
]);
//# sourceMappingURL=admission.schema.js.map